import React, { useEffect, useId, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaPalette } from 'react-icons/fa';
import '../../../styles/VibeSetPicker.css';
import { useLocale } from '../../../contexts/LocaleContext.jsx';

const PICK_ERRORS = { skin_not_owned: 'notOwned', skin_not_found: 'notFound' };

/**
 * The vibe set picker under the queue strip (ENG-812, FX-6), beside the
 * Reduce effects switch: "Vibe set" opens the listener's owned vibe sets.
 * Pointing at or focusing one previews it live on the queue; picking it saves
 * it to the account and switches the queue's look straight away. Escape, a
 * click outside or the button again closes it and takes a preview off.
 *
 * `picker` is the page's `useVibeSetPicker`; the page renders this only for
 * a signed-in listener while effects are on.
 */
const VibeSetPicker = ({ picker }) => {
  const { t } = useLocale();
  const location = useLocation();
  const panelId = useId();
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const {
    open, status, items, equippedId, listError, previewId, busyId, error,
    toggle, close, retry, preview, endPreview, pick,
  } = picker;
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      closeRef.current();
      buttonRef.current?.focus();
    };
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) closeRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  // It opens downward, below the stage and never over the player; bring it
  // into view when the stage reaches the bottom of the window.
  useEffect(() => {
    if (open) panelRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [open]);

  // Hidden with the strip (a boost pick): a preview must not stay on.
  useEffect(() => () => closeRef.current(), []);

  return (
    <div className="stream-vibe-picker" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`stream-vibe-picker-toggle${open ? ' stream-vibe-picker-toggle--open' : ''}`}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={toggle}
        data-testid="vibe-set-picker-toggle"
      >
        <FaPalette aria-hidden="true" />
        <span>{t('stream.fx.vibeSet.button')}</span>
      </button>

      {open && (
        <div
          ref={panelRef}
          id={panelId}
          className="stream-vibe-picker-panel"
          role="group"
          aria-label={t('stream.fx.vibeSet.label')}
          data-testid="vibe-set-picker"
        >
          <p className="stream-vibe-picker-hint">{t('stream.fx.vibeSet.hint')}</p>

          {status === 'loading' && items.length === 0 && (
            <p className="stream-vibe-picker-status" role="status">{t('stream.fx.vibeSet.loading')}</p>
          )}

          {status === 'error' && (
            <div className="stream-vibe-picker-status" role="alert">
              <span>{listError?.status === 401 ? t('stream.fx.vibeSet.signIn') : t('stream.fx.vibeSet.loadError')}</span>
              {listError?.status !== 401 && (
                <button type="button" className="stream-vibe-picker-link" onClick={retry}>{t('stream.fx.vibeSet.retry')}</button>
              )}
            </div>
          )}

          {error && (
            <p className="stream-vibe-picker-status" role="alert" data-testid="vibe-set-picker-error">
              {t(`stream.fx.vibeSet.errors.${PICK_ERRORS[error.code] ?? 'generic'}`)}
            </p>
          )}

          {status !== 'error' && items.length > 0 && (
            <ul className="stream-vibe-picker-list" onMouseLeave={endPreview}>
              {items.map((item) => {
                const wearing = item.id === equippedId;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`stream-vibe-picker-item${wearing ? ' stream-vibe-picker-item--wearing' : ''}${previewId === item.id ? ' stream-vibe-picker-item--preview' : ''}`}
                      aria-pressed={wearing}
                      disabled={busyId !== null}
                      onMouseEnter={() => preview(item)}
                      onFocus={() => preview(item)}
                      onClick={() => pick(item)}
                      data-testid="vibe-set-picker-item"
                    >
                      <span className="stream-vibe-picker-name">{item.name}</span>
                      {busyId === item.id && <span className="stream-vibe-picker-badge">{t('stream.fx.vibeSet.saving')}</span>}
                      {busyId !== item.id && wearing && (
                        <span className="stream-vibe-picker-badge stream-vibe-picker-badge--wearing">{t('stream.fx.vibeSet.wearing')}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <Link
            to="/shop?type=vibe_set"
            state={{ from: `${location.pathname}${location.search}` }}
            className="stream-vibe-picker-link stream-vibe-picker-shop"
            data-testid="vibe-set-picker-shop"
          >
            {t('stream.fx.vibeSet.getMore')}
          </Link>
        </div>
      )}
    </div>
  );
};

export default VibeSetPicker;
